import { Injectable, inject } from '@angular/core';
import { Observable, tap, map, catchError } from 'rxjs';
import { AssignmentStatus } from './assignment.models';
import { AssignmentApiService } from './assignment-api.service';
import { AssignmentStateService } from './assignment-state.service';

@Injectable({
  providedIn: 'root',
})
export class AssignmentPublishService {
  private readonly apiService = inject(AssignmentApiService);
  private readonly state = inject(AssignmentStateService);

  // ============================================
  // STEP 4: SETTINGS
  // ============================================

  /**
   * Save assignment settings (due date, time limit)
   */
  saveSettings(settings: {
    dueDate: string | null;
    timeLimit: number;
  }): Observable<void> {
    this.state.setError(null);

    const assignmentId = this.state.assignmentId;
    if (!assignmentId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No assignment ID'));
      });
    }

    return this.apiService.updateSettings(assignmentId, settings).pipe(
      tap(() => {
        // Set status to SETTINGS_COMPLETED to allow navigation to ready step
        this.state.setStatus(AssignmentStatus.SETTINGS_COMPLETED);
      }),
      map(() => undefined),
      catchError((err) => {
        this.state.setError(
          'Failed to save settings. Please try again.',
        );
        throw err;
      }),
    );
  }

  // ============================================
  // STEP 5: PUBLISH
  // ============================================

  /**
   * Publish assignment and get share code
   */
  publishAssignment(): Observable<void> {
    this.state.setError(null);

    const assignmentId = this.state.assignmentId;
    if (!assignmentId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No assignment ID'));
      });
    }

    return this.apiService.publishAssignment(assignmentId).pipe(
      tap((assignment) => {
        this.state.setStatus(assignment.status);
        this.state.setShareCode(assignment.shareCode || null);
      }),
      map(() => undefined),
      catchError((err) => {
        this.state.setError(
          'Failed to publish assignment. Please try again.',
        );
        throw err;
      }),
    );
  }

  // ============================================
  // ASSIGN TO USER
  // ============================================

  /**
   * Assign an unowned assignment to the current user.
   * Used after signup/login to claim an assignment created during the wizard funnel.
   */
  assignToUser(): Observable<void> {
    this.state.setError(null);

    const assignmentId = this.state.assignmentId;
    if (!assignmentId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No assignment ID'));
      });
    }

    return this.apiService.assignToUser(assignmentId).pipe(
      map(() => undefined),
      catchError((err) => {
        // Don't show error if assignment is already owned by the current user
        if (err.status !== 400) {
          this.state.setError(
            'Failed to assign assignment. Please try again.',
          );
        }
        throw err;
      }),
    );
  }
}
